/*
Notes: 
1) I'm saving query data from Admin using globalService, but this doesn't survive a page refresh.
-> alternative is to use local storage, but there are limits.
*/

import {
    AfterViewInit,
    ChangeDetectorRef,
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewChild,
} from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort, Sort } from "@angular/material/sort";
import { MatSelectChange } from "@angular/material/select";

import { MatPaginator } from "@angular/material/paginator";
import { MatTableExporterDirective } from "mat-table-exporter";

import { WorkRequestService } from "@app-services/work-request.service";
import { IWorkRequestQueryOptions, WorkRequest } from "@app-models/work-request";

import { FormFilter, KeyVal, PagingOpts, ScreenConfig} from "@app-models/common";
import { AzureUser } from "@app-models/azure-user";
import {
    getCurrentUserFromStorage,

    setFilterInStorage,
    getFilterFromStorage,
    setPreviousViewInStorage,
    setScreenModeInStorage,
} from "@app/utils/storage-utils";
import { GlobalConstants } from "@app/utils/global-constants";
import { GlobalService } from "@app-services/global.services";

import { NgxSpinnerService } from "ngx-spinner";
import { FormControl } from "@angular/forms";
import { Observable } from "rxjs";
import { UtcConverterService } from "@app/services/utc-converter.service";
@Component({
    selector: "app-work-request-view",
    templateUrl: "./work-request-view.component.html",
    styleUrls: ["./work-request-view.component.scss"],
    changeDetection: ChangeDetectionStrategy.Default,
})
export class WorkRequestViewComponent implements AfterViewInit, OnInit {
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild("workRequestListSort") workRequestListSort = new MatSort();
    @ViewChild(MatTableExporterDirective, { static: true })
    exporter: MatTableExporterDirective;

    constructor(
        private workRequestService: WorkRequestService,
        private changeDetectorRef: ChangeDetectorRef,
        private spinner: NgxSpinnerService,
        private globalService: GlobalService,
        private router: Router,
        private route: ActivatedRoute,
        private utcConverterService: UtcConverterService
    ) {


    }

    //tooltip :{} = {}

    screenConfig: { [index: string]: any } = {
        user: {
            title: "My IT Work Requests",
            instructions:
                "This screen shows a list of work requests entered by you. \
                Click anywhere in the row or use the far right 'Action' icons to edit or view an existing record." 
        },
        admin: {
            title: "IT Work Requests (Admin)",
            instructions: "",
        },
        pm: {
            title: "My Projects as IT PM",
            instructions: "This screen shows a list of Projects where you have been identified as a project manager",
        },
        sponsored: {
            title: "My Sponsored IT Work Requests",
            instructions: "This screen shows a list of Work Request where you have been identified as sponsor. Read-only access to each request is provided.",
        },        
    };


    ROWS_PER_API_CALL = 1000;
    MAX_QUERY_IF_NO_FILTER = 1000;
    loadingMessage = "";
    systemDate: Date = this.globalService.getSystemDate();
    queryPerformed = false;

    //If we separate these into different values per mode, then make this an object keyed on mode.
    displayedColumns = [
        "wrNumber",
        "status",
        "action",
    ];

    //https://stackoverflow.com/questions/56833469/typescript-error-ts7053-element-implicitly-has-an-any-type
    displayColumns: { [index: string]: any } = {
        user: [
            "wrNumber",
            "status",
            "requestTypeEnhanced",
            "subject",
            "requestorName",
            "department",  
            "createdDateShort",       
            "action",
          
        ],
        pm: [
            "wrNumber",
            "status",
            "subject",
            "projectDescription",
            "requestorName",
            "lastModifiedDateShort",   
            "action",
        ],
        sponsored: [
            "wrNumber",
            "status",
            "requestTypeEnhanced",
            "subject",
            "requestorName",
            "department", 
            "submittedDateShort",      
            "lastModifiedDateShort",      
            "action",
        ],       
        admin: [
            "wrNumber",
            "status",
            "requestTypeEnhanced",
            "consultationType",
            "subject",
            "requestorName",
            "department", 
            "submittedDateShort",          
            "lastModifiedDateShort",   
            "action",
        ],
    };

    //checkboxes will not export.
    exportColumns = [
        "wrNumber",
        "workRequestId",
        "requestType",
        "consultationType",
        "subject",
        "requestorName",
        "requestorEmail",
        "requestSponsor",
        "applicationOwner",
        "department",
        "businessRequirements",
        "desiredOutcome",
        "externalInvolvement",
        "requestNotes",
        "partOfSppOpPlan",
        "approvedBudget",
        "executiveApproval",
        "executiveApprovalName",
        "registrationRequired",
        "registrationReason",
        "traSosCompleted",
        "sensitivityLevel",
        "piaCompleted",
        "applicationType",
        "diasterRecoveryNeeded",
        "authenticationMethod",
        "numberOfUsers",
        "publicFacingURLNeeded",
        "apiRequirements",
        "milestones",

        "priorityLevel",
        "ippmStatus",
        "numberOfEnvironments",
        "tShirtSize",
        "projectMethodology",
        "projectType",
        "projectGovernance",
        "fundingSource",
        "portfolio",
        "projectCategory",
        "sppInfo",
        "checkpoint",
        "trbCheckpoint",
        "phaseInfo",
        "requestResources",
        "notes",
        "resolution",

        "projectDescription",
        "inScope",
        "outOfScope",
        "risks",
        "riskMitigation",
        "objectives",
        "decisions",
        "keyAccomplishments",
        "upcomingActivities",
        "stakeholderResources",
        "projectStartDate",
        "projectDeliveryDate",
        "projectRevisionDate",
        "projectRevisionReason",
        "projectStatus",
        "previousProjectStatus",
        "pmMilestones",

        "status",       
        "createdDateUTC",
        "azureId",
        "submittedDateUTC",
        "internalReviewDateUTC",
        "inProgressDateUTC",
        "closedDateUTC",
        "lastModifiedDateUTC",
        "lastModifiedUser",
        "lastUserAction",
    ];

    tablePageSize = 25;
    tablePageSizeOptions = [10, 25, 50, 100];
    exportTablePageSize = 500;

    currentPageSize = this.tablePageSize;

    FILTER_PREFIX = "admin_"

    FILTER_STATUS = this.FILTER_PREFIX + "status";
    FILTER_REQUEST_TYPE = this.FILTER_PREFIX + "requesttype";
    FILTER_CONSULTATION_TYPE = this.FILTER_PREFIX + "consultationtype";

    FILTER_WORK_REQUEST_ID = this.FILTER_PREFIX + "workrequestid";
    FILTER_REQUESTOR = this.FILTER_PREFIX + "requestor";

    PAGE_SIZE_LENGTH = this.FILTER_PREFIX + "pagesize";
    
    statusFormFilter: FormFilter = {
        name: this.FILTER_STATUS,
        label: "Status",
        options: [],
        searchCtrl: new FormControl(''),
        selectCtrl: new FormControl<string[]>([]),
        filteredOptions: new Observable<Record<string, string>[]>(),
    }
    requestTypeFormFilter: FormFilter = {
        name: this.FILTER_REQUEST_TYPE,
        label: "Request Type",
        options: [],
        searchCtrl: new FormControl(''),
        selectCtrl: new FormControl<string[]>([]),
        filteredOptions: new Observable<Record<string, string>[]>(),
    }
    consultationTypeFormFilter: FormFilter = {
        name: this.FILTER_CONSULTATION_TYPE,
        label: "Consultation Type",
        options: [],
        searchCtrl: new FormControl(''),
        selectCtrl: new FormControl<string[]>([]),
        filteredOptions: new Observable<Record<string, string>[]>(),
    }
    filterWorkRequestId: FormControl = new FormControl('');
    filterRequestor: FormControl = new FormControl('');

    tableFilter: FormControl = new FormControl(null);

    //For filterselects, make sure this is an array
    filterDefaults: any = {
        admin_status: [GlobalConstants.STATUS_SUBMITTED],
    };
  
    screenMode = GlobalConstants.REQUEST_FORM_MODE_USER;
    screenTitles: ScreenConfig  = this.screenConfig[this.screenMode];
    btnAllowNewRequest = false;

    filterDictionary = new Map<string, string>();

    formFilters: FormFilter[] = [];
    workRequest?: WorkRequest;

    workRequestList: MatTableDataSource<WorkRequest>;

    currentUser: AzureUser;

    //---------------------------------------------------------------
    // Lifecycle events
    //---------------------------------------------------------------
    ngOnInit(): void {
        this.workRequestList = new MatTableDataSource<WorkRequest>();
        this.currentUser = getCurrentUserFromStorage();

        this.screenMode = GlobalConstants.REQUEST_FORM_MODE_USER;

        this.route.data.subscribe(data => {
            this.screenMode = data["mode"];
            this.setupScreenMode();
        })




        /*
        this.route.url.subscribe((paths) => {
            if (paths.length > 1) {
                const finalPath = paths[paths.length - 1].path;
                this.screenMode = finalPath;
            }
            this.setupScreenMode();
        });
        */
    }
    ngAfterViewInit() {
        this.setupSortAndFilters();

    }

    //---------------------------------------------------------------
    // Filter modules
    //---------------------------------------------------------------

    setupScreenMode() {
        setScreenModeInStorage(this.screenMode);
        this.screenTitles = this.screenConfig[this.screenMode];
        if (this.screenMode === GlobalConstants.REQUEST_FORM_MODE_USER) {
            this.getWorkRequestList();
            this.btnAllowNewRequest = true;
           
        } //For PM mode, get all for now.. what happens when it becomes big 
        else if (this.screenMode === GlobalConstants.REQUEST_FORM_MODE_PM) {
            this.btnAllowNewRequest = false;
            this.getWorkRequestList();
        }
        else if (this.screenMode === GlobalConstants.REQUEST_FORM_MODE_SPONSORED) {
            this.btnAllowNewRequest = false;
            this.getWorkRequestList();
        }        
        //if admin mode and there is saved data, then use it.
        else if (this.screenMode === GlobalConstants.REQUEST_FORM_MODE_ADMIN) {
            this.btnAllowNewRequest = false;
            if (this.globalService.hasStoredData(GlobalConstants.ADMIN_DATA_STORED_KEY)) {
                this.workRequestList.data = this.globalService.getStoredData(GlobalConstants.ADMIN_DATA_STORED_KEY)

            }
            //this.getWorkRequestListAdmin();
        }
        

        this.displayedColumns = this.displayColumns[this.screenMode].map(
            (x: any) => x
        );


        //Including the default value above only sets the widget, it doesn't actually do the filtering

       
    }
    setupSortAndFilters() {
        this.workRequestList.paginator = this.paginator;
        
        this.workRequestList.sort = this.workRequestListSort;


        this.workRequestList.sortingDataAccessor = (item, property) => {
            switch (property) {
              case 'submittedDateShort': return new Date(item.submittedDateUTC||'');
              case 'createdDateShort': return new Date(item.createdDateUTC||'');
              case 'lastModifiedDateShort': return new Date(item.lastModifiedDateUTC||'');
              case 'requestTypeEnhanced': return item.requestType||'';
              default: return item[property];
            }
          };

          
          //let sortState : Sort = { active: "wrNumber", direction: "desc" };
          //use submitted date for admin
          let sortState: Sort = { active: "createdDateShort", direction: "desc" };
          if (this.screenMode === GlobalConstants.REQUEST_FORM_MODE_ADMIN){
            sortState = { active: "submittedDateShort", direction: "desc" };
          }
          
          this.workRequestListSort.active = sortState.active;
          this.workRequestListSort.direction = sortState.direction;
          this.workRequestListSort.sortChange.emit(sortState);
        
        this.workRequestList.filterPredicate = function (record, filter) {
            if (JSON.stringify(record).toLowerCase().indexOf(filter)!=-1) {
                console.log(JSON.stringify(record));
                return true;
            } else {
                return false;
            }
        };
        

        this.tableFilter?.valueChanges.subscribe(() => {
            //this.applyTableSearch();
            let filterValue = this.tableFilter.value?.trim(); // Remove whitespace
            filterValue = filterValue?.toLowerCase()||''; // MatTableDataSource defaults to lowercase matches
            this.workRequestList.filter = filterValue; 
        });
        
        const statusValues = GlobalConstants.statusValues.map((x) => x);
        //const requestTypeValue = GlobalConstants.requestTypeValues.map((x) => x);
       // const consultationTypeValue = GlobalConstants.consultationTypeValues.map((x) => x);
       const requestTypeValue: KeyVal[] = [
        { 'key': GlobalConstants.REQUEST_TYPE_PROJECT, 'value': GlobalConstants.REQUEST_TYPE_PROJECT },
        { 'key': GlobalConstants.REQUEST_TYPE_CONSULTATION, 'value': GlobalConstants.REQUEST_TYPE_CONSULTATION },
        { 'key': GlobalConstants.REQUEST_TYPE_WORK_ORDER, 'value': GlobalConstants.REQUEST_TYPE_WORK_ORDER },
    ];
        const consultationTypeValue: KeyVal[] = [
                { key: "Technical", value: "Technical" },
                { key: "Privacy Legislative", value: "Privacy Legislative" },
                { key: "Privacy Technical", value: "Privacy Technical" },
            ];
        //Note: dont use all if I'm using a multi-select.
        //statusValues.unshift({ key: "", value: "All" });
        this.statusFormFilter = {
            name: this.FILTER_STATUS,
            label: "Status",
            options: statusValues ,
            searchCtrl: new FormControl(''),
            selectCtrl: new FormControl<string[]>(this.getFilterValue(this.FILTER_STATUS)),
            filteredOptions: new Observable<Record<string, string>[]>()     
        }
        this.requestTypeFormFilter = {
            name: this.FILTER_REQUEST_TYPE,
            label: "Request Type",
            options: requestTypeValue ,
            searchCtrl: new FormControl(''),
            selectCtrl: new FormControl<string[]>(this.getFilterValue(this.FILTER_REQUEST_TYPE)),
            filteredOptions: new Observable<Record<string, string>[]>()     
        }
        this.consultationTypeFormFilter = {
            name: this.FILTER_CONSULTATION_TYPE,
            label: "Consultation Type",
            options: consultationTypeValue ,
            searchCtrl: new FormControl(''),
            selectCtrl: new FormControl<string[]>(this.getFilterValue(this.FILTER_CONSULTATION_TYPE)),
            filteredOptions: new Observable<Record<string, string>[]>()     
        }
        this.filterRequestor.setValue(this.getFilterValueString(this.FILTER_REQUESTOR));
        this.filterWorkRequestId.setValue(this.getFilterValueString(this.FILTER_WORK_REQUEST_ID));

        const storedPageLen = this.getFilterValueString(this.PAGE_SIZE_LENGTH);
        if (storedPageLen) {
            this.tablePageSize = storedPageLen;
        }

    }

    //Return the value from storage if it existed, or retuen the default value.
    //check for nulls, since one of the filters is a boolean
    public getFilterValueString(filter: string) {
        const selectVal = getFilterFromStorage(filter);
        let retStr = this.filterDefaults[filter];
        if (selectVal !== null) {
            retStr = String(selectVal);
        }
        return retStr;
    }

    public getFilterValue(filter: string): string[] {
        let retStr: string[] = [];
        const selectVal = getFilterFromStorage(filter);
        if (selectVal) {
            retStr = selectVal.toString().split(",");
        }
        return retStr;
    }

    clearSearch() {
        this.tableFilter.setValue(null);
    }

    setRowIcon(wr: WorkRequest) {
        if ((this.screenMode === GlobalConstants.REQUEST_FORM_MODE_USER && wr.status != GlobalConstants.STATUS_DRAFT) ||
             (this.screenMode === GlobalConstants.REQUEST_FORM_MODE_SPONSORED) || 
            ( wr.status == GlobalConstants.STATUS_CLOSED) ) {
                return 'notes';
            } else {
                return 'edit'
            } 
        }

    updateFilterState() {
             
        let values: string[] = this.statusFormFilter.selectCtrl.value || ([] as string[]);
        setFilterInStorage(this.FILTER_STATUS, values.join(','));
        values = this.requestTypeFormFilter.selectCtrl.value || ([] as string[]);
        setFilterInStorage(this.FILTER_REQUEST_TYPE, values.join(','));
        values = this.consultationTypeFormFilter.selectCtrl.value || ([] as string[]);
        setFilterInStorage(this.FILTER_CONSULTATION_TYPE, values.join(','));
        if (this.filterWorkRequestId.value != null && this.filterWorkRequestId.value.trim() != "") {           
            setFilterInStorage(this.FILTER_WORK_REQUEST_ID, this.filterWorkRequestId.value.trim());
        } else {
            setFilterInStorage(this.FILTER_WORK_REQUEST_ID, '');
        }
        if (this.filterRequestor.value != null && this.filterRequestor.value.trim() != "") {           
            setFilterInStorage(this.FILTER_REQUESTOR, this.filterRequestor.value.trim());
        } else {
            setFilterInStorage(this.FILTER_REQUESTOR, '');
        }     

       
    }

    clearWorkRequestIdFilter() {
        this.filterWorkRequestId.setValue(null);
        this.updateFilterState();
    }
    clearRequestorFilter() {
        this.filterRequestor.setValue(null);
        this.updateFilterState();
    }
    //------------------------------------------
    // Click Events
    //------------------------------------------


    applyFormFilter(ob: MatSelectChange, formfilter: FormFilter) {
        //---setFilterInStorage(formfilter.name, formfilter.defaultValue);
        this.filterDictionary.set(formfilter.name, ob.value);
        this.setFilter();
    }

    // remove "vw_" from values as they need to match column names.

    setFilter() {
        const jsonString = JSON.stringify(
            Array.from(this.filterDictionary.entries())
        );
        const re = /vw_/gi;
        this.workRequestList.filter = jsonString.replace(re, "");
    }


    getWorkRequestList(): void {
        this.spinner.show();
        this.workRequestList.data = [];
        this.spinner.show();
        const pagingOpts = { pageNumber: 1, pageSize: this.ROWS_PER_API_CALL, nextUrl: "" };
        this.workRequestService.getWorkRequestList(this.screenMode, pagingOpts,{}).subscribe({
            next: (entries: any) => {
                this.workRequestList.data = [...this.workRequestList.data, ...entries.data];
                this.spinner.hide();
                this.queryPerformed = true;
            },
            error: () => {
                this.spinner.hide();
            },
        });
    }


    getWorkRequestListAdmin(): void {

        //this.continueLongLoad = false;
       // this.longLoad = true;
        this.workRequestList.data = [];
        this.spinner.show();
        //let pagingOpts = { pageNumber: 1, pageSize: this.ROWS_PER_API_CALL, nextUrl: "" };
        const pagingOpts = { pageNumber: 1, pageSize: this.ROWS_PER_API_CALL, nextUrl: "" };
        const opts : IWorkRequestQueryOptions = {};
       
        //let formFilter = this.formFilters.get(this.FILTER_VENDOR);
        if (this.statusFormFilter && this.statusFormFilter.selectCtrl.value && this.statusFormFilter.selectCtrl.value?.length > 0 &&
            this.statusFormFilter.selectCtrl.value?.length != this.statusFormFilter.options.length) {
            opts.status = this.statusFormFilter.selectCtrl.value;
        }
        if (this.requestTypeFormFilter && this.requestTypeFormFilter.selectCtrl.value && this.requestTypeFormFilter.selectCtrl.value?.length > 0 &&
            this.requestTypeFormFilter.selectCtrl.value?.length != this.requestTypeFormFilter.options.length) {
            opts.requestType = this.requestTypeFormFilter.selectCtrl.value;
        }
        if (this.consultationTypeFormFilter && this.consultationTypeFormFilter.selectCtrl.value && this.consultationTypeFormFilter.selectCtrl.value?.length > 0 &&
            this.consultationTypeFormFilter.selectCtrl.value?.length != this.consultationTypeFormFilter.options.length) {
            opts.consultationType = this.consultationTypeFormFilter.selectCtrl.value;
        }
        if (this.filterWorkRequestId.value) {
            opts.workRequestId = this.filterWorkRequestId.value.trim();
        }

        if (this.filterRequestor.value) {
            opts.requestor = this.filterRequestor.value.trim();
        }
   
        this.getWorkRequestListAdminPage(pagingOpts, opts);
        this.queryPerformed = true;

    }

    getWorkRequestListAdminPage(pagingOpts: PagingOpts, opts: IWorkRequestQueryOptions): void {
        this.spinner.show();
        //
       
        this.workRequestService.getWorkRequestList("",pagingOpts, opts).subscribe({
            next: (entries: any) => {
                this.workRequestList.data = [...this.workRequestList.data, ...entries.data];
                this.globalService.storeData(GlobalConstants.ADMIN_DATA_STORED_KEY,this.workRequestList.data );
                //this.processEntries(entries.data);
                this.spinner.hide();
                if (entries.nextPage) {
                    //this.expandFilters = false;
                    const currentTotal = entries.pageNumber * entries.pageSize;
                    this.loadingMessage = 'Loaded: ' + currentTotal.toLocaleString() + ' of ' + entries.totalRecords.toLocaleString() + ' records...';
                    //this.alertService.warn(this.loadingMessage, {autoClose: true});
                    pagingOpts.nextUrl = entries.nextPage;
                    if (Object.keys(opts).length > 0 || 
                       (Object.keys(opts).length == 0 && currentTotal < this.MAX_QUERY_IF_NO_FILTER)) {
                        this.getWorkRequestListAdminPage(pagingOpts, opts);
                    } else {
                        this.loadingMessage = ""; 
                    }
                }  else {
               
                this.loadingMessage = "";
                
            }
            },
            error: () => {
                this.spinner.hide();
            },
        });
    }

 

    editWorkRequest(workRequest: WorkRequest): void {
        setPreviousViewInStorage(this.router.url);
        /*
        let route = "/my-work-request/" + workRequest.workRequestId;
        if (this.screenMode === GlobalConstants.REQUEST_FORM_MODE_ADMIN) {
            route = "/work-request/" + workRequest.workRequestId;
        }
        */
        const route = "/work-request/" + workRequest.workRequestId;
        this.router.navigateByUrl(route, {});
    }
    gotoWorkRequestStatusReport(workRequest: WorkRequest): void {
        setPreviousViewInStorage(this.router.url);
        const route = "/work-request/" + workRequest.workRequestId + "/status";
        
        this.router.navigateByUrl(route, {});
    }    
    addWorkRequest(): void {
        setPreviousViewInStorage(this.router.url);
        const route = "/work-request/new";
        this.router.navigateByUrl(route, {});
    }
   
    //---------------------------------------------------------------
    // Utilities
    //---------------------------------------------------------------

    formatMilestones(obj: any, addRevision : boolean) {
        let ret = "";
        if (obj == null || obj.length === 0) {
            return "";
        } else {
            for (const i in obj) {
                ret += obj[i]['description'] + " : Date " + obj[i]['date'].substring(0,10);
                if (addRevision) {
                    ret += (obj[i]['revisionDate'] != null ? " : RevisionDate " + obj[i]['revisionDate'].substring(0,10)  + (obj[i]['revisionReason'] ? obj[i]['revisionReason']: "")  : "");
                }
                ret += ", ";
            }
            return ret.substring(0, ret.length - 2);
        }
   }

   //There will onlt be one..but..
    formatResource(obj: any, addRole : boolean) {
        let ret = "";
        if (obj == null || obj.length === 0 || obj[0]['name'] === '') {
            return "";
        } else {

            for (const i in obj) {
                ret += obj[i]['name']  + ( obj[i]['email'] ?  " : " + obj[i]['email'] : "");
                if (addRole) {
                    ret += " : " + obj[i]['role'];
                }
                ret += ", ";
            }
            return ret.substring(0, ret.length - 2);

        }
    }  

    formatRequestType(wr: WorkRequest) {
        //If the request is deleted, in the view the entry is still in the table, so set the value to blank
        if (wr.requestType === undefined || wr.requestType === null) {
            return "";
        }
        if (wr.requestType !== GlobalConstants.REQUEST_TYPE_PROJECT && wr.checkpoint !== null && wr.checkpoint !== '') {
            return wr.requestType + "<br>(" + wr.checkpoint + ")";
        } else if (wr.requestType === GlobalConstants.REQUEST_TYPE_PROJECT && wr.projectStatus)  {
            return wr.requestType + "<br>(" + wr.projectStatus + ")";
       } else {
            return wr.requestType;
        }
        

    }
    rowClass(row: WorkRequest) {
        let ret = "progress-bar-" + (row.status ? row.status?.toLowerCase().replace(' ', '-') : '');

        let currentDate = this.globalService.getSystemDateUTC();
       
        if (row.submittedDateUTC != null) {
            let submittedDate = new Date(row.submittedDateUTC);
            let dateDiff = this.globalService.dateDiff(currentDate, submittedDate);
            let hrsDiff = dateDiff.hours;
            //console.log(currentDate, submittedDate, hrsDiff);
            //If today is monday or tuesday, ignore 48 hrs (weekend) in elapsed time.
            //If you are using this app on a saturday or sunday, same concept. saturday -- ignore saturday 24hr or sunday ignore 48 hrs.
            //since we are only dealing with 2 days response, we don't care about determining 'business days' for entire period.
            if (currentDate.getDay() === 1 || currentDate.getDay() === 2) { hrsDiff -= 48; }
            if (currentDate.getDay() === 6) { hrsDiff -= 24; }
            if (currentDate.getDay() === 0) { hrsDiff -= 48; }
            //console.log(currentDate, submittedDate, hrsDiff);
            if (this.screenMode === GlobalConstants.REQUEST_FORM_MODE_ADMIN && row.status === GlobalConstants.STATUS_SUBMITTED && hrsDiff > GlobalConstants.OVERDUE_RESPONSE_HOURS) {
                ret += " overdue-response";
            }
        }


        return ret;
    }

    //This is all oaging events, but we will use it for storing pagesize.
    listingPageSizeChange(evt: any) {
        setFilterInStorage(this.PAGE_SIZE_LENGTH, evt.pageSize.toString());
    }

    //this is standard, but we need to ovveride
    //<button mat - raised - button(click)="exporter.exportTable('txt')" > Txt < /button>
    exportData(format: any) {
        //Change the columns shown in the table. The exporter will only export shown columns
        //We want the pagesize of the table to be large to scroll through it faster.
        this.spinner.show();
        this.displayedColumns = this.exportColumns.map((x) => x);
        this.currentPageSize = this.tablePageSize;
        this.tablePageSize = this.exportTablePageSize;

        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
            this.exporter.exportTable(format, {
                fileName:
                    this.screenTitles.title +
                    `-${this.systemDate.toISOString()}`,
                Props: {
                    Author: GlobalConstants.siteTitle,
                },
            });
        }, 100);
        
    }

    /**
    * event when export is  completed.  reset the display
    */
    exportCompleted() {
        this.displayedColumns = this.displayColumns[this.screenMode].map(
            (x: any) => x
        );

        //Swal.fire("Export Completed.")
        this.tablePageSize = this.currentPageSize;
        //this.changeDetectorRef.detectChanges();
        this.spinner.hide();
    }

}
